import { useMemo } from 'react'
import { useAuth } from '../contexts/AuthContext'
import { useCollection } from './useFirestore'

/**
 * Hook to get ciphers available to the current user.
 *
 * Access rules:
 * - Admin (isOwner) sees all ciphers
 * - Regular users see ciphers known by their linked characters
 *
 * @returns {{ ciphers: Array, loading: boolean }}
 */
export function useUserCiphers() {
  const { user, isOwner } = useAuth()
  const { data: allCiphers, loading: ciphersLoading } = useCollection('ciphers', { orderByField: 'sortOrder' })
  const { data: allCharacters, loading: charsLoading } = useCollection('characters')

  const loading = ciphersLoading || charsLoading

  const ciphers = useMemo(() => {
    if (loading) return []

    // Admin sees all ciphers
    if (isOwner) return allCiphers

    if (!user) return []

    // Get cipher IDs from characters linked to this user
    const userCharacters = allCharacters.filter(c => c.linkedUserId === user.uid)
    const cipherIds = new Set(userCharacters.flatMap(c => c.knownCipherIds || []))

    // Filter ciphers to only those the user has access to
    return allCiphers.filter(cipher => cipherIds.has(cipher.id))
  }, [user, isOwner, allCiphers, allCharacters, loading])

  return { ciphers, loading }
}
